import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import * as Yup from 'yup';
import { Formik, useFormikContext, getIn } from 'formik';
import { Button, Grid } from '@material-ui/core';
import {
  FarmRiskType,
  FarmScheduleCoverageTypesByState,
  lookupsJson,
  ScheduledFarmItemTypes,
  FarmScheduleCoverageType
} from '@ourbranch/lookups';

import { addYears } from 'date-fns';
import { brandConfig } from 'brand-config';
import { BrandFormField } from 'common/components/farm/brand-form-field';
import InfoIcon from 'core/assets/svg/ic-info-dark.svg?react';
import CollapsibleCard from 'core/components/collapsible-card';
import Field from 'core/components/form/form.v2';
import Label from 'core/components/label/label';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import { useActiveFormState } from 'common/hooks/use-active-form-state';
import {
  ValidationErrorsModal,
  useValidationErrorsModal,
  setTouchedFromErrors
} from 'core/components/validation-errors-modal';
import { Animals4HFFAForm, FarmIncomeCoverageForm, InlandMarineForm, PeakSeasonInventoryForm } from '../../sub-risks';
import ScheduledFarmPropertyItem from './scheduled-farm-property-item';
import { getWindHailOptions } from './wind-hail-deductible-options';
import { MACHINERY_CATEGORIES, OPEN_PERILS_CATEGORIES, RCC_CATEGORIES } from './scheduled-farm-property-constants';
import useStyles from './FO6-scheduled.styles';

const { deductibleAllOther, earthquakeDeductible, scheduledFarmPropertyCategories } = lookupsJson.farm;
const eqDeductibleOptions = earthquakeDeductible.filter((d) => d.id !== 0.02);

const riskType = FarmRiskType.Fo6Scheduled;

const initialValues = {
  category: undefined,
  description: undefined,
  deductible: undefined,
  deductibleWindHail: undefined,
  limit: undefined,
  serialNumber: undefined,
  make: undefined,
  year: undefined,
  hasReplacementCostCoverage: false,
  hasOpenPerils: false,
  increaseStackLimit: undefined,
  livestockCategory: undefined,
  includeWinterPeril: undefined,
  hasWinterPerils: false,
  hasSuffocationOfLivestock: false,
  hasSpecialCattleEndorsement: false
};

const currentYear = new Date().getFullYear();

const getValidationSchema = (covFConfig) =>
  Yup.object().shape({
    category: Yup.string().required('Category is required'),
    description: Yup.string().required('Description is required'),
    deductible: Yup.number().required('Deductible is required'),
    limit: Yup.number().required('Coverage limit is required').positive('Coverage limit must be greater than 0'),
    year: Yup.number().when('category', {
      is: (val) => covFConfig?.itemOverrides?.[val]?.showYear,
      then: (schema) =>
        schema
          .required('Year is required')
          .min(1900, 'Year must be 1900 or later')
          .max(currentYear + 1, `Year must be ${currentYear + 1} or earlier`)
    }),
    serialNumber: Yup.string().when('category', {
      is: (val) => {
        const overrides = covFConfig?.itemOverrides?.[val];
        if (overrides?.serialNumberRequired === false) {
          return false;
        }
        return val === ScheduledFarmItemTypes.COMBINES || val === ScheduledFarmItemTypes.CUSTOM_FARM_MACHINERY;
      },
      then: (schema) => schema.required('Serial number is required')
    }),
    make: Yup.string().when('category', {
      is: (val) => {
        const overrides = covFConfig?.itemOverrides?.[val];
        if (overrides?.hideMakeField) {
          return false;
        }
        return val === ScheduledFarmItemTypes.COMBINES || val === ScheduledFarmItemTypes.CUSTOM_FARM_MACHINERY;
      },
      then: (schema) => schema.required('Make is required')
    }),
    livestockCategory: Yup.string().when('category', {
      is: ScheduledFarmItemTypes.LIVESTOCK,
      then: (schema) => schema.required('Livestock category is required')
    }),
    additionalPerils: Yup.number().when('category', {
      is: (val) => val === ScheduledFarmItemTypes.LIVESTOCK && !covFConfig?.itemOverrides?.[val]?.hideAdditionalPerils,
      then: (schema) => schema.required('Additional perils option is required')
    })
  });

// Recursively checks whether an errors object contains any actual error messages (strings).
// Needed because deleting a leaf error from a nested object leaves empty parent containers.
const hasNestedErrors = (errors) => {
  if (typeof errors === 'string') {
    return true;
  }
  if (Array.isArray(errors)) {
    return errors.some(hasNestedErrors);
  }
  if (errors && typeof errors === 'object') {
    return Object.values(errors).some(hasNestedErrors);
  }
  return false;
};

/**
 * Inner form fields for the "Add Item" form, extracted as a proper component
 * so that React hooks (useMemo, useEffect) are called at the top level
 * rather than inside the Formik render prop callback.
 */
const AddItemFormFields = ({
  covFConfig,
  disableFormFinal,
  disableForm,
  availableCoverageFCategories,
  availableScheduledCoverageTypes
}) => {
  const classes = useStyles();
  const { submitForm, values, setFieldValue } = useFormikContext();

  const overrides = covFConfig?.itemOverrides?.[values.category];

  const deductibleOptions =
    values.category === ScheduledFarmItemTypes.LIVESTOCK && covFConfig?.livestockDeductibleOptions
      ? covFConfig.livestockDeductibleOptions
      : covFConfig?.deductibleOptions || deductibleAllOther;

  const defaultDeductible =
    values.category === ScheduledFarmItemTypes.LIVESTOCK && covFConfig?.defaultLivestockDeductible
      ? covFConfig.defaultLivestockDeductible
      : covFConfig?.defaultDeductible || deductibleOptions[0]?.id;

  const showMachineryFields = MACHINERY_CATEGORIES.includes(values.category) && !overrides?.hideMakeField;

  const showSerialNumber = MACHINERY_CATEGORIES.includes(values.category);

  const showRCC = RCC_CATEGORIES.includes(values.category) && !overrides?.hideReplacementCost;

  const showOpenPerils = (() => {
    if (overrides?.hideOpenPerils) {
      return false;
    }
    if (overrides?.openPerilsAlwaysOn) {
      return true;
    }
    return OPEN_PERILS_CATEGORIES.includes(values.category);
  })();

  const windHailResult = useMemo(
    () =>
      covFConfig?.showWindHailDeductible
        ? getWindHailOptions({ coverage: values.limit, allPerilDeductible: values.deductible })
        : null,
    [values.limit, values.deductible, covFConfig?.showWindHailDeductible]
  );

  useEffect(() => {
    if (values.category) {
      setFieldValue('deductible', defaultDeductible);
      if (!values.limit) {
        setFieldValue('limit', 0);
      }
    }
  }, [values.category]);

  // Auto-set wind/hail deductible when options change
  useEffect(() => {
    if (windHailResult?.targetValue != null) {
      const validIds = new Set(windHailResult.options.map((o) => o.id));
      if (!validIds.has(values.deductibleWindHail)) {
        setFieldValue('deductibleWindHail', windHailResult.targetValue);
      }
    }
  }, [windHailResult?.targetValue, windHailResult?.options]);

  // Sync always-on overrides with Formik state
  useEffect(() => {
    if (overrides?.openPerilsAlwaysOn && !values.hasOpenPerils) {
      setFieldValue('hasOpenPerils', true);
    }
  }, [overrides?.openPerilsAlwaysOn]);

  useEffect(() => {
    if (overrides?.winterPerilsAlwaysOn && !values.hasWinterPerils) {
      setFieldValue('hasWinterPerils', true);
    }
  }, [overrides?.winterPerilsAlwaysOn]);

  return (
    <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
      <Field
        xs={6}
        type="select"
        id="category"
        name="category"
        mode="light"
        label="Type of item"
        options={availableCoverageFCategories}
        disabled={disableFormFinal}
      />
      <Field
        xs={6}
        type="string"
        id="scheduledItemName"
        name="description"
        mode="light"
        label={overrides?.descriptionLabel || 'Item name'}
        disabled={disableFormFinal}
      />
      <Field
        xs={6}
        type="select"
        id="deductible"
        name="deductible"
        mode="light"
        label="Deductible"
        options={deductibleOptions}
        disabled={disableFormFinal}
      />
      <Field
        xs={6}
        type="numeric"
        id="limit"
        name="limit"
        mode="light"
        label="Coverage limit"
        format={{ thousandSeparator: true, prefix: '$' }}
        disabled={disableFormFinal}
      />

      {overrides?.showYear && (
        <Field xs={6} type="numeric" id="year" name="year" mode="light" label="Year" disabled={disableFormFinal} />
      )}

      {covFConfig?.showWindHailDeductible && windHailResult && (
        <Field
          xs={6}
          type="select"
          id="deductibleWindHail"
          name="deductibleWindHail"
          mode="light"
          label="Wind/hail deductible"
          options={windHailResult.options}
          disabled={disableFormFinal || windHailResult.isOverride}
        />
      )}

      {showSerialNumber && (
        <Field
          xs={6}
          type="string"
          id="serialNumber"
          name="serialNumber"
          mode="light"
          label="Serial number"
          disabled={disableFormFinal}
        />
      )}

      {showMachineryFields && (
        <Field xs={6} type="string" id="make" name="make" mode="light" label="Make/model" disabled={disableFormFinal} />
      )}

      {showRCC && (
        <Field
          xs={6}
          type="checkbox"
          id="hasReplacementCostCoverage"
          name="hasReplacementCostCoverage"
          mode="light"
          label="Replacement cost coverage"
          disabled={disableFormFinal}
        />
      )}

      {showOpenPerils && (
        <Field
          xs={6}
          type="checkbox"
          id="hasOpenPerils"
          name="hasOpenPerils"
          mode="light"
          label={overrides?.openPerilsAlwaysOn ? 'Includes intake of foreign objects' : 'Open perils'}
          subLabel={overrides?.openPerilsAlwaysOn ? undefined : 'Including intake of foreign objects'}
          disabled={disableFormFinal || overrides?.openPerilsAlwaysOn}
        />
      )}

      {values.category === ScheduledFarmItemTypes.LIVESTOCK && (
        <>
          <Field
            xs={6}
            type="select"
            id="livestockCategory"
            name="livestockCategory"
            mode="light"
            label="Livestock type"
            options={lookupsJson.farm.livestockTypes}
            disabled={disableFormFinal}
            onChange={(val) => {
              if (val !== 'cattle') {
                setFieldValue('hasSpecialCattleEndorsement', false);
              }
            }}
          />
          {!overrides?.hideAdditionalPerils && (
            <Field
              xs={6}
              type="select"
              id="additionalPerils"
              name="additionalPerils"
              mode="light"
              label="Additional Perils"
              options={lookupsJson.farm.livestockAdditionalPerils}
              disabled={disableFormFinal}
            />
          )}
          <Field
            xs={6}
            type="checkbox"
            id="hasWinterPerils"
            name="hasWinterPerils"
            mode="light"
            label="Winter Perils - Livestock"
            disabled={disableFormFinal || overrides?.winterPerilsAlwaysOn}
          />
          {values.hasWinterPerils && (
            <Field
              xs={6}
              type="numeric"
              id="winterPerilsLimit"
              name="limit"
              mode="light"
              label="Winter Perils - Limit"
              format={{ thousandSeparator: true, prefix: '$' }}
              disabled={true}
            />
          )}
          {!overrides?.hideSuffocationOfLivestock && (
            <Field
              xs={6}
              type="checkbox"
              id="hasSuffocationOfLivestock"
              name="hasSuffocationOfLivestock"
              mode="light"
              label="Suffocation of livestock"
              disabled={disableFormFinal}
            />
          )}
          {!overrides?.hideSpecialCattleEndorsement && (
            <Field
              xs={6}
              type="checkbox"
              id="hasSpecialCattleEndorsement"
              name="hasSpecialCattleEndorsement"
              mode="light"
              label="Special cattle endorsement"
              subLabel="Multiple loss coverage"
              disabled={disableForm || values.livestockCategory !== 'cattle'}
            />
          )}
        </>
      )}

      <Grid
        container
        justifyContent={values.category === ScheduledFarmItemTypes.LIVESTOCK ? 'space-between' : 'flex-end'}
        className={classes.sectionFooter}
      >
        {values.category === ScheduledFarmItemTypes.LIVESTOCK &&
          availableScheduledCoverageTypes?.includes(FarmScheduleCoverageType.ANIMALS_4H_FFA) && (
            <Grid className={classes.livestockInfoTextContainer} xs={9}>
              <InfoIcon width={16} height={16} />
              <span className={classes.livestockInfoText}>
                This is for livestock only. If you'd like to add animals (Under 4-H & FFA programs), please go to that
                section.
              </span>
            </Grid>
          )}
        <Button
          type="button"
          mode="big"
          variant="contained"
          color="secondary"
          className={classes.button}
          disabled={disableFormFinal}
          onClick={submitForm}
        >
          Add Item
        </Button>
      </Grid>
    </Grid>
  );
};

const ScheduledFarmProperty = observer(({ disabled, fieldName, riskIndex }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched, validateForm, setErrors, setTouched } = useFormikContext();
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();

  const locationRisks = getIn(values, `${fieldName}.risks`);

  const coverageFieldName = `${fieldName}.risks[${riskIndex}].coverages.coverageF`;
  const scheduledItems = getIn(values, coverageFieldName) || [];

  const address = getIn(values, `${fieldName}.address`);

  const riskFieldName = `${fieldName}.risks[${riskIndex}]`;

  const { disableForm, setActiveForm } = useActiveFormState(riskFieldName);
  const disableFormFinal = Boolean(disableForm || disabled);

  const covFConfig = brandConfig.farm[address.state]?.[riskType]?.coverageF;

  // Policy end date: use expirationDate (policy context) or calculate from farmEffectiveDate (offer context)
  const farmEffectiveDate = values.global?.farmEffectiveDate;
  const policyEndDate =
    values.expirationDate ||
    (farmEffectiveDate ? addYears(new Date(farmEffectiveDate), 1).toISOString().split('T')[0] : undefined);

  const availableCoverageFCategories = covFConfig?.itemTypes || scheduledFarmPropertyCategories;
  const availableScheduledCoverageTypes =
    brandConfig.farm[address.state]?.[riskType]?.availableScheduledCoverageTypes ||
    FarmScheduleCoverageTypesByState[address.state] ||
    [];

  const validationSchema = useMemo(() => getValidationSchema(covFConfig), [covFConfig]);

  const handleRemoveRisk = () => {
    const newRisks = locationRisks.filter((_, index) => index !== riskIndex);
    setFieldValue(`${fieldName}.risks`, newRisks);
    setFieldTouched(`${fieldName}.risks`);
    setActiveForm(null);
  };

  return (
    <>
      <ValidationErrorsModal open={showValidationErrorsModal} onClose={() => setShowValidationErrorsModal(false)} />
      <CollapsibleCard
        content={
          <div className={classes.header}>
            <div className={classes.headerRow}>
              <Label type="infoTitleSmall">FO-6 Scheduled farm property</Label>
            </div>
          </div>
        }
        contentRight={
          <Field id={fieldName} name={fieldName} type="remove" disabled={disableFormFinal} onClick={handleRemoveRisk} />
        }
        initialOpen={true}
        id={`${fieldName}.FO6Scheduled`}
      >
        <Grid className={classes.coverageCard}>
          <FormSectionWrapper
            sectionTitle="Scheduled farm property (F)"
            totalLabel="items"
            totalCount={scheduledItems?.length || 0}
            emptyStateTitle="There are no items added yet"
            mode="light"
            containerClassName={classes.noBorderContainer}
          >
            {/* list items */}
            {scheduledItems.map((item, index) => (
              <ScheduledFarmPropertyItem
                key={`farm-property-${index}`}
                item={item}
                index={index}
                fieldName={fieldName}
                coverageType="coverageF"
                onDelete={(itemIndex) => {
                  const newItems = scheduledItems.filter((_, checkIndex) => checkIndex !== itemIndex);
                  setFieldValue(coverageFieldName, newItems);
                  setFieldTouched(coverageFieldName);
                }}
                disabled={disableFormFinal}
              />
            ))}

            {/* totals row */}
            {scheduledItems.length > 0 && (
              <div className={classes.totalsItem}>
                <div className={classes.totalsItemTitleRow}>
                  <Grid xs={12}>
                    <div className={classes.totalsItemName}>
                      <InfoIcon width={16} height={16} />
                      Totals
                    </div>
                  </Grid>
                  <Grid className={classes.totalsItemStack} xs={3}>
                    <span className={classes.totalsItemLabel}>Deductible</span>
                    <span className={classes.totalsItemValue}>
                      ${scheduledItems.reduce((total, item) => total + (item.deductible || 0), 0).toLocaleString()}
                    </span>
                  </Grid>
                  <div className={classes.totalsDivider} />
                  <Grid className={classes.totalsItemStack} xs={3}>
                    <span className={classes.totalsItemLabel}>Coverage</span>
                    <span className={classes.totalsItemValue}>
                      ${scheduledItems.reduce((total, item) => total + (item.limit || 0), 0).toLocaleString()}
                    </span>
                  </Grid>
                </div>
              </div>
            )}

            {/* add form */}
            <div className={classes.addFormContainer}>
              <Formik
                initialValues={initialValues}
                validateOnChange={false}
                validateOnBlur={false}
                validationSchema={validationSchema}
                onSubmit={async (newItem, { resetForm }) => {
                  const parentErrors = await validateForm();
                  // Exclude the coverageF required-array error to prevent add-item deadlock (#29083):
                  // The validation requires at least one coverageF item, but it fires before the
                  // first item can be added, making it impossible to add items to an empty array.
                  const coveragesErrors = getIn(parentErrors, `${fieldName}.risks[${riskIndex}].coverages`);
                  if (typeof coveragesErrors?.coverageF === 'string' && scheduledItems.length === 0) {
                    delete coveragesErrors.coverageF;
                  }
                  if (hasNestedErrors(parentErrors)) {
                    setErrors(parentErrors);
                    setTouched(setTouchedFromErrors(parentErrors));
                    setShowValidationErrorsModal(true);
                    return;
                  }

                  const overrides = covFConfig?.itemOverrides?.[newItem.category];

                  // Force always-on coverages
                  if (overrides?.openPerilsAlwaysOn) {
                    newItem.hasOpenPerils = true;
                  }
                  if (overrides?.winterPerilsAlwaysOn) {
                    newItem.hasWinterPerils = true;
                  }

                  Object.keys(newItem).forEach((key) => {
                    if (newItem[key] === '') {
                      newItem[key] = null;
                    }
                  });

                  const currentItems = scheduledItems || [];
                  setFieldValue(coverageFieldName, [...currentItems, newItem]);
                  setFieldTouched(coverageFieldName);
                  resetForm();
                }}
              >
                <AddItemFormFields
                  covFConfig={covFConfig}
                  disableFormFinal={disableFormFinal}
                  disableForm={disableForm}
                  availableCoverageFCategories={availableCoverageFCategories}
                  availableScheduledCoverageTypes={availableScheduledCoverageTypes}
                />
              </Formik>
            </div>
          </FormSectionWrapper>
        </Grid>
        <Grid container spacing={2} className={classes.greenCoverageCard}>
          <Grid item xs={12}>
            <Label type="formSubTitle">Coverages</Label>
          </Grid>

          <BrandFormField
            xs={12}
            id={`${fieldName}.risks[${riskIndex}].coverages.deductibleEarthquake`}
            name={`${fieldName}.risks[${riskIndex}].coverages.deductibleEarthquake`}
            type="select"
            options={eqDeductibleOptions}
            mode="dark"
            label="Earthquake deductible"
            disabled={disableForm}
            riskType={riskType}
            state={address.state}
          />

          <BrandFormField
            xs={6}
            name={`${fieldName}.risks[${riskIndex}].coverages.expandedDebrisRemoval`}
            id={`${fieldName}.risks[${riskIndex}].coverages.expandedDebrisRemoval`}
            type="checkbox"
            label="Expanded debris removal"
            mode="dark"
            disabled={disableForm}
            riskType={riskType}
            state={address.state}
          />

          <BrandFormField
            xs={6}
            name={`${fieldName}.risks[${riskIndex}].coverages.hasSnowWeightCoverage`}
            id={`${fieldName}.risks[${riskIndex}].coverages.hasSnowWeightCoverage`}
            type="checkbox"
            label="Weight of ice, snow, or sleet coverage"
            mode="dark"
            disabled={disableForm}
            riskType={riskType}
            state={address.state}
          />

          {/* Animals 4-H & FFA */}
          {availableScheduledCoverageTypes.includes(FarmScheduleCoverageType.ANIMALS_4H_FFA) && (
            <Grid item xs={12}>
              <Animals4HFFAForm
                fieldName={`${fieldName}.risks[${riskIndex}].coverages.scheduleCoverages`}
                disabled={disableForm}
              />
            </Grid>
          )}

          {/* Farm Income Coverage */}
          {availableScheduledCoverageTypes.includes(FarmScheduleCoverageType.INCOME) && (
            <Grid item xs={12}>
              <FarmIncomeCoverageForm
                fieldName={`${fieldName}.risks[${riskIndex}].coverages.scheduleCoverages`}
                disabled={disableForm}
              />
            </Grid>
          )}

          {/* Inland Marine Coverage */}
          {availableScheduledCoverageTypes.includes(FarmScheduleCoverageType.SPP) && (
            <Grid item xs={12}>
              <InlandMarineForm
                fieldName={`${fieldName}.risks[${riskIndex}].coverages.scheduleCoverages`}
                disabled={disableForm}
                riskType={riskType}
                coverages={getIn(values, `${fieldName}.risks[${riskIndex}].coverages`) || {}}
              />
            </Grid>
          )}

          {/* Peak Season Inventory */}
          {availableScheduledCoverageTypes.includes(FarmScheduleCoverageType.PEAK) && (
            <Grid item xs={12}>
              <PeakSeasonInventoryForm
                fieldName={`${fieldName}.risks[${riskIndex}].coverages.scheduleCoverages`}
                disabled={disableForm}
                state={address.state}
                scheduledItems={scheduledItems}
                policyEndDate={policyEndDate}
              />
            </Grid>
          )}
        </Grid>
      </CollapsibleCard>
    </>
  );
});

ScheduledFarmProperty.propTypes = {
  disabled: PropTypes.bool,
  fieldName: PropTypes.string.isRequired,
  riskIndex: PropTypes.number.isRequired
};

ScheduledFarmProperty.defaultProps = {
  disabled: false
};

export default ScheduledFarmProperty;
